import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import EmojiPicker from 'emoji-picker-react';
import axios from 'axios';
import MediaPreview from './MediaPreview';
import './ChatArea.css';

const getApiUrl = () => {
    if (window.location.hostname !== 'localhost') return window.location.origin;
    return window.location.port === '3001' ? 'http://localhost:3000' : 'http://localhost:3000';
};

const API_URL = getApiUrl();

const CHANNELS = [
  { id: '922986590894796', name: 'Comercial', icon: '🏢', color: '#0078FF' },
  { id: '999551239899325', name: 'Administrativo', icon: '👔', color: '#6C47FF' },
  { id: '916595048209652', name: 'RR.II.', icon: '🌎', color: '#00B4D8' },
  { id: '1020657397786989', name: 'Becarios', icon: '📈', color: '#00C896' },
  { id: '983548531506198', name: 'Regulares', icon: '⭐', color: '#FFB300' },
  { id: '1308381959018456', name: 'Peru', icon: '🇵🇪', color: '#FF4081' },
  { id: '1000790306449643', name: 'Info 2', icon: 'ℹ️', color: '#FF6B6B' },
  { id: '1233806963139486', name: 'Académico', icon: '🎓', color: '#9B59B6' },
  { id: '1232012809998778', name: 'Logística', icon: '📦', color: '#E67E22' },
  { id: '1348031501732308', name: 'ONG', icon: '🤝', color: '#10B981' },
];

function ChatArea({ conversation, messages, onSendMessage, isMobile, onBack, tags, onUpdateTags, onCreateTag, onDeleteTag, onUpdateChannel, onOpenTagSelector, onSendReaction, quickReplies, onDeleteQuickReply, onSendImageByUrl, onToggleBotStatus, globalBotEnabled, onLoadMoreMessages, hasMoreMessages, loadingMoreMessages }) {
  const quickRepliesList = Array.isArray(quickReplies) ? quickReplies : [];
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showChannelMenu, setShowChannelMenu] = useState(false);
  const [hoveredMessage, setHoveredMessage] = useState(null); // track hovered message for reaction button
  const [showReactionMenu, setShowReactionMenu] = useState(false); // Sub-menu state for reactions
  const [showFullEmojiPickerForReaction, setShowFullEmojiPickerForReaction] = useState(false); // Full picker for reactions
  const [reactionMenuMessageId, setReactionMenuMessageId] = useState(null); // track which message has reaction menu open
  const [replyingTo, setReplyingTo] = useState(null); // Menaje al que se responde
  const [previewFile, setPreviewFile] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null); // Resaltado temporal al ir al mensaje citado (estilo WhatsApp)
  const highlightTimeoutRef = useRef(null);
  const textareaRef = useRef(null);
  const [showQuickRepliesDropdown, setShowQuickRepliesDropdown] = useState(false);
  const quickRepliesListRef = useRef(null);
  // Dropdown "Etiquetar chat" (panel oscuro tipo WhatsApp)
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagDropdownSelection, setTagDropdownSelection] = useState([]);
  const tagButtonRef = useRef(null);
  const tagDropdownRef = useRef(null);

  // Estados y refs para grabación de audio
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error al acceder al micrófono:', error);
      alert('No se pudo acceder al micrófono. Verifica los permisos.');
    }
  };

  const stopRecording = (shouldSend = true) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = () => {
        // Detener las pistas
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());

        if (shouldSend) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const file = new File([audioBlob], `nota_de_voz_${Date.now()}.webm`, { type: 'audio/webm' });
          handleConfirmSendMedia(file, '');
        }
      };

      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    setRecordingTime(0);
  };

  const cancelRecording = () => {
    stopRecording(false);
  };

  const formatRecordingTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleConfirmSendMedia = useCallback(async (file, caption) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversationId', conversation.id);

    // Determine Type
    let type = 'document';
    if (file.type.includes('sticker') || file.type.includes('webp')) {
      type = 'sticker';
    } else if (file.type.startsWith('image/')) {
      type = 'image';
    } else if (file.type.startsWith('video/')) {
      type = 'video';
    } else if (file.type.startsWith('audio/')) {
      type = 'audio';
    }
    formData.append('type', type);

    if (replyingTo) {
      formData.append('replyToMessageId', replyingTo.id);
    }
    if (caption) {
      formData.append('caption', caption);
    }

    try {
      setSending(true);
      await axios.post(`${API_URL}/api/messages/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReplyingTo(null);
      setPreviewFile(null); // Close modal
      setShowEmojiPicker(false);
    } catch (error) {
      console.error('Error enviando archivo:', error);
      alert('Error enviando archivo: ' + (error.response?.data?.error || error.message));
    } finally {
      setSending(false);
    }
  }, [conversation?.id, replyingTo, API_URL]);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const prevMessagesLengthRef = useRef(0); // para distinguir prepend vs append
  const prevScrollHeightRef = useRef(0);   // para restaurar posición al prepend



  // Sincronizar selección del dropdown con las etiquetas del chat al abrir
  useEffect(() => {
    if (showTagDropdown && conversation?.tags) {
      setTagDropdownSelection(conversation.tags.map(t => t.id));
    }
  }, [showTagDropdown, conversation?.id, conversation?.tags]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    if (!showTagDropdown) return;
    const handleClickOutside = (e) => {
      if (
        tagButtonRef.current && !tagButtonRef.current.contains(e.target) &&
        tagDropdownRef.current && !tagDropdownRef.current.contains(e.target)
      ) {
        setShowTagDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTagDropdown]);

  const sortedTagsForDropdown = useMemo(() =>
    [...(tags || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [tags]
  );

  const toggleTagInDropdown = (tagId) => {
    setTagDropdownSelection(prev =>
      prev.map(String).includes(String(tagId)) 
        ? prev.filter(id => String(id) !== String(tagId)) 
        : [...prev, tagId]
    );
  };

  const handleSaveTagDropdown = () => {
    if (conversation?.id) onUpdateTags(conversation.id, tagDropdownSelection);
    setShowTagDropdown(false);
  };

  // Auto-scroll al fondo: solo cuando llega un mensaje NUEVO (append), no al cargar más viejos (prepend)
  useEffect(() => {
    if (messages.length === 0) return;
    const isAppend = messages.length > prevMessagesLengthRef.current;
    const isPrepend = messages.length > prevMessagesLengthRef.current &&
      messages[0]?.id !== undefined &&
      prevMessagesLengthRef.current > 0;

    // Al cambiar de conversación: scroll instantáneo al fondo
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    prevMessagesLengthRef.current = messages.length;
  }, [conversation?.id]);

  useEffect(() => {
    if (messages.length === 0) {
      prevMessagesLengthRef.current = 0;
      return;
    }

    const container = messagesContainerRef.current;
    const added = messages.length - prevMessagesLengthRef.current;

    if (added > 0) {
      // Determinar si fueron mensajes nuevos al final (append) o viejos al inicio (prepend)
      // Si el primer mensaje cambió, es prepend (carga de más viejos)
      const firstMsgChanged = prevMessagesLengthRef.current > 0 && added > 1;

      if (firstMsgChanged && container) {
        // Restaurar posición de scroll después de prepend
        const scrollDiff = container.scrollHeight - prevScrollHeightRef.current;
        container.scrollTop = container.scrollTop + scrollDiff;
      } else {
        // Mensaje nuevo al final: smooth scroll
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }

    prevMessagesLengthRef.current = messages.length;
    if (container) prevScrollHeightRef.current = container.scrollHeight;
  }, [messages.length]);

  // Scroll handler para infinite scroll (cargar más viejos al llegar al top)
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop < 80 && hasMoreMessages && !loadingMoreMessages && conversation?.id) {
        prevScrollHeightRef.current = container.scrollHeight;
        onLoadMoreMessages && onLoadMoreMessages(conversation.id);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMoreMessages, loadingMoreMessages, conversation?.id, onLoadMoreMessages]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [conversation?.id]);

  const handleSend = useCallback(async () => {
    if (!messageText.trim() || sending) return;

    setSending(true);
    try {
      await onSendMessage(messageText, replyingTo);
      setMessageText('');
      setReplyingTo(null); // Limpiar respuesta
      setShowEmojiPicker(false); // Close emoji picker after sending
      // Reset textarea height back to default
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      alert('Error al enviar el mensaje. Intenta de nuevo.');
    } finally {
      setSending(false);
    }
  }, [messageText, sending, onSendMessage, replyingTo]);

  const handleKeyPress = (e) => {
    if (showQuickRepliesDropdown && e.key === 'Escape') {
      setShowQuickRepliesDropdown(false);
      setMessageText(prev => prev.replace(/\/[^/]*$/, ''));
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Mostrar menú de respuestas rápidas al escribir /
  useEffect(() => {
    const endsWithSlash = /\/[^/]*$/.test(messageText);
    setShowQuickRepliesDropdown(endsWithSlash && quickRepliesList.length > 0);
  }, [messageText, quickRepliesList.length]);

  const selectQuickReply = (q) => {
    if (q.image_url && onSendImageByUrl && conversation?.id) {
      onSendImageByUrl(conversation.id, q.image_url, q.text || '', replyingTo);
      setMessageText(prev => prev.replace(/\/[^/]*$/, '').trim());
      setReplyingTo(null);
      setShowQuickRepliesDropdown(false);
      return;
    }
    const text = typeof q === 'string' ? q : (q?.text || '');
    setMessageText(prev => prev.replace(/\/[^/]*$/, '').trimEnd() + (prev.trimEnd() && !prev.trimEnd().endsWith(' ') ? ' ' : '') + text);
    setShowQuickRepliesDropdown(false);
  };

  // Helper para identificar el canal
  const getChannelName = (channelId) => {
    const commercialId = '922986590894796';
    const adminId = '999551239899325';
    const rrioId = '916595048209652';
    const ventasId = '1020657397786989';
    const regularesId = '983548531506198';
    const logisticaId = '1232012809998778';

    if (channelId === commercialId) return '🏢 Comercial';
    if (channelId === adminId) return '👔 Administrativo';
    if (channelId === rrioId) return '🌎 R. Internacionales';
    if (channelId === ventasId) return '📈 Becarios';
    if (channelId === regularesId) return '⭐ Regulares';
    if (channelId === logisticaId) return '📦 Logística';
    if (channelId === '1308381959018456') return '🇵🇪 Peru';
    if (channelId === '1000790306449643') return 'ℹ️ Info 2';
    if (channelId === '1233806963139486') return '🎓 Académico';
    if (channelId === '1348031501732308') return '🤝 ONG';
    return '📱 WhatsApp'; // Default
  };

  // ... (skipping unchanged code)

  // eslint-disable-next-line no-lone-blocks
  {
    showChannelMenu && (
      <div className="channel-dropdown-menu">
        <button
          className={`channel-option ${conversation.channel_id === '922986590894796' ? 'active' : ''}`}
          onClick={() => {
            onUpdateChannel(conversation.id, '922986590894796');
            setShowChannelMenu(false);
          }}
        >
          🏢 Comercial
        </button>
        <button
          className={`channel-option ${conversation.channel_id === '999551239899325' ? 'active' : ''}`}
          onClick={() => {
            onUpdateChannel(conversation.id, '999551239899325');
            setShowChannelMenu(false);
          }}
        >
          👔 Administrativo
        </button>
        <button
          className={`channel-option ${conversation.channel_id === '916595048209652' ? 'active' : ''}`}
          onClick={() => {
            onUpdateChannel(conversation.id, '916595048209652');
            setShowChannelMenu(false);
          }}
        >
          🌎 R. Internacionales
        </button>
        <button
          className={`channel-option ${conversation.channel_id === '1020657397786989' ? 'active' : ''}`}
          onClick={() => {
            onUpdateChannel(conversation.id, '1020657397786989');
            setShowChannelMenu(false);
          }}
        >
          📈 Becarios
        </button>
        <button
          className={`channel-option ${conversation.channel_id === '983548531506198' ? 'active' : ''}`}
          onClick={() => {
            onUpdateChannel(conversation.id, '983548531506198');
            setShowChannelMenu(false);
          }}
        >
          ⭐ Regulares
        </button>
      </div>
    )
  }

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/La_Paz'
    });
  };

  const renderContent = (msg) => {
    // Helper para usar URL absoluta del backend
    const getMediaUrl = (path) => {
      if (!path) return '';
      if (path.startsWith('http')) return path; // Ya es absoluta

      // Mapear '/media/...' al endpoint API para evitar el caché estático de Hostinger (LiteSpeed)
      if (path.startsWith('/media/')) {
        const fileName = path.split('/').pop();
        return `${API_URL}/api/files?f=${fileName}`;
      }

      // Normalizar slashes para evitar problemas en Windows (fallback)
      const normalizedPath = path.replace(/\\/g, '/');

      if (normalizedPath.startsWith('/')) return `${API_URL}${normalizedPath}`; // Es relativa (/media/...)
      return `${API_URL}/${normalizedPath}`;
    };

    // Helper renderizado condicional del contenido del mensaje
    const messageContent = (() => {
      if (msg.type === 'text') {
        if (msg.body && msg.body.startsWith('/media/')) {
          const imgSrc = getMediaUrl(msg.body);
          return (
            <div style={{ maxWidth: '300px', overflow: 'hidden' }}>
              <img
                src={imgSrc}
                alt="Imagen"
                style={{ maxWidth: '100%', borderRadius: '8px', cursor: 'pointer' }}
                onClick={() => window.open(imgSrc, '_blank')}
                onError={(e) => {
                  const maxRetries = 4;
                  const retries = parseInt(e.target.dataset.retries || '0', 10);
                  if (retries < maxRetries) {
                    e.target.dataset.retries = retries + 1;
                    const delay = Math.pow(2, retries) * 1000;
                    setTimeout(() => {
                      const base = imgSrc.split('?_t=')[0];
                      e.target.src = base + (base.includes('?') ? '&' : '?') + '_t=' + Date.now();
                    }, delay);
                  }
                }}
              />
            </div>
          );
        }
        const text = (msg.body ?? '').trim();
        return <div style={{ minHeight: '1em' }}>{text || '\u00A0'}</div>;
      }
      if (msg.type === 'image' || msg.type === 'sticker') {
        const imgSrc = getMediaUrl(msg.body);
        const isSticker = msg.type === 'sticker' || (msg.body && msg.body.includes('.webp'));
        const caption = (msg.caption || '').trim();
        return (
          <div style={{ maxWidth: isSticker ? '150px' : '300px', overflow: 'hidden' }}>
            <img
              src={imgSrc}
              alt="Imagen"
              style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px', cursor: 'pointer' }}
              onClick={() => window.open(imgSrc, '_blank')}
              onError={(e) => {
                const maxRetries = 4;
                const retries = parseInt(e.target.dataset.retries || '0', 10);
                if (retries < maxRetries) {
                  e.target.dataset.retries = retries + 1;
                  const delay = Math.pow(2, retries) * 1000; // 1s, 2s, 4s, 8s
                  setTimeout(() => {
                    // Forzar recarga cambiando la URL con timestamp
                    const base = imgSrc.split('?_t=')[0];
                    e.target.src = base + (base.includes('?') ? '&' : '?') + '_t=' + Date.now();
                  }, delay);
                } else {
                  // Después de 4 reintentos fallidos, mostrar placeholder
                  e.target.style.display = 'none';
                  const placeholder = document.createElement('div');
                  placeholder.style.cssText = 'padding:12px;background:rgba(0,0,0,0.06);border-radius:8px;font-size:13px;color:#888;text-align:center';
                  placeholder.textContent = '📷 Imagen no disponible';
                  e.target.parentNode.appendChild(placeholder);
                }
              }}
            />
            {caption ? <div style={{ marginTop: '6px', fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{caption}</div> : null}
          </div>
        );
      }
      if (msg.type === 'video') {
        const videoSrc = getMediaUrl(msg.body);
        const caption = (msg.caption || '').trim();
        return (
          <div style={{ maxWidth: '300px' }}>
            <video controls style={{ width: '100%', borderRadius: '8px' }}><source src={videoSrc} type="video/mp4" /></video>
            {caption ? <div style={{ marginTop: '6px', fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{caption}</div> : null}
          </div>
        );
      }
      if (msg.type === 'document') {
        const docSrc = getMediaUrl(msg.body);
        const rawFileName = msg.body ? msg.body.split('/').pop() : 'documento';
        // Limpiar prefijo numérico largo (mediaId_) si existe para mostrar un nombre amigable
        const cleanDisplayName = rawFileName.replace(/^\d{10,25}_/, '');
        const caption = (msg.caption || '').trim();
        const isPdf = rawFileName.toLowerCase().endsWith('.pdf');
        return (
          <div style={{ maxWidth: '300px' }}>
            <div
              onClick={() => window.open(docSrc, '_blank')}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: 'rgba(0,0,0,0.05)', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.08)' }}
              title="Abrir o descargar documento"
            >
              <div style={{ fontSize: '28px' }}>{isPdf ? '📕' : '📄'}</div>
              <div style={{ overflow: 'hidden', flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '190px' }}>{cleanDisplayName}</div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{isPdf ? 'Ver PDF / Descargar' : 'Clic para abrir'}</div>
              </div>
            </div>
            {caption ? <div style={{ marginTop: '6px', fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{caption}</div> : null}
          </div>
        );
      }
      if (msg.type === 'audio') {
        const audioSrc = getMediaUrl(msg.body);
        return <div style={{ minWidth: '280px' }}><audio controls style={{ width: '100%', height: '40px' }}><source src={audioSrc} /></audio></div>;
      }
      const fallback = (msg.body ?? '').trim() || '\u00A0';
      return <div style={{ minHeight: '1em' }}>{fallback}</div>;
    })();

    return (
      <div className={`message-content-wrapper msg-type-${msg.type}`} style={{ position: 'relative' }}
        onMouseEnter={() => setHoveredMessage(msg.id)}
        onMouseLeave={() => setHoveredMessage(null)}
      >
        {/* Cita: "Respondiendo a [nombre/mensaje]" — visible como en WhatsApp */}
        {(msg.reply_to_message_id ?? msg.replyToMessageId) && (() => {
          const replyId = msg.reply_to_message_id ?? msg.replyToMessageId;
          const replyToMsg = messages.find(m => m.id === replyId);
          const rawName = replyToMsg
            ? (replyToMsg.direction === 'outgoing' ? 'Tú' : (conversation.name || conversation.wa_id || 'Contacto'))
            : null;
          const replyAuthorName = (rawName && String(rawName).trim() && String(rawName) !== '.') ? rawName : (replyToMsg ? 'Contacto' : null);
          const rawPreview = replyToMsg
            ? (replyToMsg.type === 'text' || replyToMsg.type === 'template'
              ? (replyToMsg.body || '')
              : replyToMsg.type === 'image' || replyToMsg.type === 'sticker'
                ? '📷 Imagen'
                : replyToMsg.type === 'video'
                  ? '🎥 Video'
                  : replyToMsg.type === 'audio'
                    ? '🎵 Audio'
                    : replyToMsg.type === 'document'
                      ? '📄 Documento'
                      : 'Mensaje')
            : '';
          const replyPreview = (rawPreview && String(rawPreview).trim()) ? rawPreview : (replyToMsg ? 'Mensaje de texto' : 'un mensaje anterior');
          const replyPreviewTruncated = replyPreview.length > 60 ? replyPreview.slice(0, 60) + '...' : replyPreview;

          return (
            <div
              className="message-reply-quote"
              style={{
                borderLeft: `3px solid ${msg.direction === 'outgoing' ? 'rgba(0,80,60,0.9)' : '#00a884'}`,
                padding: '6px 10px 6px 12px',
                marginBottom: '6px',
                borderRadius: '0 6px 6px 0',
                cursor: 'pointer',
                background: msg.direction === 'outgoing' ? 'rgba(0,90,70,0.25)' : 'rgba(0,168,132,0.12)',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                minWidth: 0,
                maxWidth: '100%'
              }}
              onClick={() => {
                if (!replyToMsg?.id) return;
                if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
                setHighlightedMessageId(replyToMsg.id);
                const el = document.getElementById(`msg-${replyToMsg.id}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                highlightTimeoutRef.current = setTimeout(() => {
                  setHighlightedMessageId(null);
                  highlightTimeoutRef.current = null;
                }, 2500);
              }}
            >
              <div style={{
                fontWeight: '600',
                fontSize: '12px',
                color: msg.direction === 'outgoing' ? '#fff' : '#00a884',
                lineHeight: 1.2,
                textShadow: msg.direction === 'outgoing' ? '0 0 1px rgba(0,0,0,0.2)' : 'none'
              }}>
                {replyAuthorName ? `Respondiendo a ${replyAuthorName}` : 'Respondiendo a mensaje anterior'}
              </div>
              <div style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                fontSize: '13px',
                color: msg.direction === 'outgoing' ? 'rgba(255,255,255,0.95)' : '#111b21',
                lineHeight: 1.3
              }}>
                {replyPreviewTruncated}
              </div>
            </div>
          );
        })()}

        {messageContent}

        {/* Action Trigger (WhatsApp Style) */}
        {/* Action Trigger (WhatsApp Style) */}
        {(hoveredMessage === msg.id || messageMenuId === msg.id) && (
          <div
            className={`message-action-trigger ${messageMenuId === msg.id ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setMessageMenuId(messageMenuId === msg.id ? null : msg.id);
            }}
          >
            <svg className="trigger-icon" viewBox="0 0 24 24">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        )}

        {renderMessageMenu(msg)}
      </div>
    );
  };

  const [messageMenuId, setMessageMenuId] = useState(null); // ID of message with open menu
  const [editingMessage, setEditingMessage] = useState(null); // Message being edited

  useEffect(() => {
    const handleClickOutside = () => setMessageMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleEdit = (msg) => {
    setEditingMessage(msg);
    setMessageText(msg.type === 'text' ? msg.body : (msg.caption || ''));
    setMessageMenuId(null);
    // Focus input
    setTimeout(() => {
      const textarea = document.querySelector('.input-container textarea');
      if (textarea) textarea.focus();
    }, 100);
  };

  const handleSendEdited = async () => {
    if (!editingMessage || !messageText.trim()) return;

    const originalId = editingMessage.id;
    // True Edit Flow
    try {
      await axios.put(`${API_URL}/api/messages/${originalId}`, {
        newText: messageText
      });
      // Local state update handled by socket 'message_updated'
    } catch (error) {
      console.error("Error editing message:", error);
      alert('Error al editar mensaje (quizás pasó el tiempo límite)');
    }

    setEditingMessage(null);
  };

  // Import CSS separately to ensure styles are applied
  // Note: Standard import at top is preferred, but for this specific block edit:
  // We assume specific styling is now in ChatArea.css, which should be imported at top of file. 
  // For this replace, I will just update the render function to use the new classes.

  // Reset submenu when opening new menu
  useEffect(() => {
    if (messageMenuId) setShowReactionMenu(false);
  }, [messageMenuId]);

  const renderMessageMenu = (msg) => {
    if (messageMenuId !== msg.id) return null;

    // Determine position based on message direction to prevent overflow
    const isOutgoing = msg.direction === 'outgoing';
    const menuStyle = {
      right: isOutgoing ? '0' : 'auto',
      left: !isOutgoing ? '0' : 'auto',
    };

    const renderReactionPicker = () => (
      <div className="reaction-picker-container">
        <div className="reaction-header">
          <span className="back-btn" onClick={(e) => { e.stopPropagation(); setShowReactionMenu(false); setShowFullEmojiPickerForReaction(false); }}>←</span>
          <span>Reaccionar</span>
        </div>
        {!showFullEmojiPickerForReaction ? (
          <div className="reaction-grid">
            {['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '👏', '🎉', '💡', '✅', '❌'].map(emoji => (
              <span
                key={emoji}
                className="reaction-option"
                onClick={() => { onSendReaction(msg.id, emoji); setMessageMenuId(null); }}
                title={`Reaccionar con ${emoji}`}
              >
                {emoji}
              </span>
            ))}
            <span
              className="reaction-option plus-btn"
              onClick={(e) => { e.stopPropagation(); setShowFullEmojiPickerForReaction(true); }}
              title="Más reacciones"
            >
              ➕
            </span>
          </div>
        ) : (
          <div className="reaction-full-picker">
            <EmojiPicker
              onEmojiClick={(emojiData) => {
                onSendReaction(msg.id, emojiData.emoji);
                setMessageMenuId(null);
                setShowFullEmojiPickerForReaction(false);
              }}
              autoFocusSearch={false}
              theme="light"
              width="100%"
              height="300px"
              searchDisabled={false}
              skinTonesDisabled
              previewConfig={{ showPreview: false }}
            />
          </div>
        )}
      </div>
    );

    const renderMainMenu = () => (
      <>
        <div className="menu-item" onClick={() => { alert('Info no implementado aún'); setMessageMenuId(null); }}>
          <svg className="menu-icon" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
          <span>Info. del mensaje</span>
        </div>
        <div className="menu-item" onClick={() => { setReplyingTo(msg); setMessageMenuId(null); }}>
          <svg className="menu-icon" viewBox="0 0 24 24"><polyline points="9 14 4 9 9 4"></polyline><path d="M20 20v-7a4 4 0 0 0-4-4H4"></path></svg>
          <span>Responder</span>
        </div>
        <div className="menu-item" onClick={() => {
          navigator.clipboard.writeText(msg.type === 'text' ? msg.body : (msg.caption || ''));
          setMessageMenuId(null);
          alert('Copiado');
        }}>
          <svg className="menu-icon" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
          <span>Copiar</span>
        </div>

        <div className="menu-item" onClick={(e) => { e.stopPropagation(); setShowReactionMenu(true); }}>
          <svg className="menu-icon" viewBox="0 0 24 24"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <span>Reaccionar</span>
            <span style={{ fontSize: '10px', color: '#999' }}>▶</span>
          </div>
        </div>

        {msg.direction === 'outgoing' && msg.status !== 'revoked' && (
          <>
            <div className="menu-item" onClick={() => handleEdit(msg)}>
              <svg className="menu-icon" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
              <span>Editar</span>
            </div>
            <div className="menu-item danger" onClick={async () => {
              if (window.confirm('¿Eliminar mensaje para todos?')) {
                try {
                  await axios.delete(`${API_URL}/api/messages/${msg.id}/revoke`);
                } catch (error) { alert('Error eliminando mensaje'); }
              }
              setMessageMenuId(null);
            }}>
              <svg className="menu-icon" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
              <span>Eliminar</span>
            </div>
          </>
        )}
      </>
    );

    return (
      <div
        className="message-dropdown-menu"
        style={menuStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {showReactionMenu ? renderReactionPicker() : renderMainMenu()}
      </div>
    );
  };





  if (!conversation) {
    return (
      <div className="chat-area no-chat-selected-page">
        <div className="background-animate"></div>
        <div className="no-chat-selected-container">
          <div className="no-chat-selected-card">
            <div className="no-chat-logo">
              <svg viewBox="0 0 24 24" width="60" height="60" fill="white">
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.891.524 3.662 1.435 5.176L2.05 21.95l4.855-1.274A9.956 9.956 0 0 0 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18c-1.734 0-3.346-.51-4.704-1.388l-.337-.213-2.864.752.766-2.785-.233-.37C3.766 14.73 3 13.197 3 12c0-4.97 4.03-9 9-9s9 4.03 9 9-4.03 9-9 9z" />
                <path d="M8 10h8v2H8zm0 4h5v2H8z" />
              </svg>
            </div>
            <h1 className="no-chat-title">EduServer CRM</h1>
            <p className="no-chat-subtitle">Selecciona una conversación para comenzar a chatear con tus clientes de forma profesional.</p>
            <div className="no-chat-hint">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
              </svg>
              <span>Tus mensajes están sincronizados en tiempo real</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getInitial = (name) => name ? name.charAt(0).toUpperCase() : '?';

  return (
    <div className="chat-area fade-in-up" key={conversation.id}>
      <header className="chat-header">
        <div className="chat-header-info">
          <button className="chat-header-back" onClick={onBack}>
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <div className="chat-avatar">
            {getInitial(conversation.name)}
          </div>
          <div className="chat-user-details">
            <h3>{(conversation.name && conversation.name !== 'undefined' ? conversation.name : conversation.wa_id) || 'Contacto'}</h3>
            {conversation.wa_id && conversation.wa_id !== 'undefined' && (
              <span className="chat-status">{conversation.wa_id}</span>
            )}
            {conversation.tags && conversation.tags.length > 0 && (
              <div className="header-tags">
                {conversation.tags.map(tag => (
                  <span key={tag.id} className="tag-badge" style={{ backgroundColor: tag.color || '#00a884' }}>
                    {tag.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="chat-header-actions">
          {/* Channel Switcher */}
          <div className="dropdown-container">
            <button
              onClick={() => setShowChannelMenu(!showChannelMenu)}
              className="header-btn"
            >
              <span style={{ fontSize: '16px' }}>{CHANNELS.find(c => String(c.id) === String(conversation.channel_id))?.icon || '📱'}</span>
              {CHANNELS.find(c => String(c.id) === String(conversation.channel_id))?.name || 'WhatsApp'}
              <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" strokeWidth="2.5" fill="none" style={{ marginLeft: '4px' }}>
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {showChannelMenu && (
              <div className="dropdown-menu">
                {CHANNELS.map(ch => (
                  <div
                    key={ch.id}
                    className={`dropdown-item ${String(conversation.channel_id) === String(ch.id) ? 'selected' : ''}`}
                    onClick={() => {
                      onUpdateChannel(conversation.id, ch.id);
                      setShowChannelMenu(false);
                    }}
                  >
                    {ch.icon} {ch.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bot Status Toggle */}
          {(() => {
            const isSchedulingChannel = String(conversation.channel_id) === '1233806963139486';
            const isBotActive = conversation.bot_status === 'bot' || !conversation.bot_status;
            const displayActive = globalBotEnabled || isSchedulingChannel;

            return (
              <button
                onClick={() => {
                  if (!displayActive) {
                    alert('La IA está desactivada de forma GLOBAL. Actívala en el menú de la izquierda (robot 🤖) o en Mensajes Automáticos.');
                    return;
                  }
                  onToggleBotStatus(conversation.id, conversation.bot_status || 'human');
                }}
                className={`header-btn ${isBotActive ? 'active' : ''} ${!displayActive ? 'global-off' : ''}`}
                style={{
                  opacity: displayActive ? 1 : 0.7,
                  backgroundColor: !displayActive ? 'rgba(255, 152, 0, 0.1)' : undefined,
                  borderColor: !displayActive ? '#ff9800' : undefined,
                  color: !displayActive ? '#e65100' : undefined,
                }}
              >
                <span style={{ fontSize: '16px' }}>{displayActive ? '🤖' : '⚠️'}</span>
                {displayActive 
                  ? (isBotActive ? 'Bot Activo' : 'Modo Humano')
                  : 'IA Global OFF'
                }
              </button>
            );
          })()}

          {/* Etiquetar Button */}
          <div className="dropdown-container">
            <button
              onClick={() => setShowTagDropdown(!showTagDropdown)}
              ref={tagButtonRef}
              className={`header-btn ${showTagDropdown ? 'active' : ''}`}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" style={{ color: '#f5bc4b' }}>
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
              <span>Etiquetar</span>
            </button>

            {showTagDropdown && (
              <div ref={tagDropdownRef} className="dropdown-menu" style={{ width: '220px' }}>
                <div style={{ padding: '8px 16px', fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '8px', color: '#111b21' }}>Etiquetar chat</div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {sortedTagsForDropdown.length === 0 ? (
                    <div style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#666' }}>No hay etiquetas</div>
                  ) : (
                    sortedTagsForDropdown.map(tag => (
                      <div key={tag.id} className="dropdown-item" onClick={(e) => { e.stopPropagation(); toggleTagInDropdown(tag.id); }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: tag.color }}></div>
                        <span style={{ flex: 1 }}>{tag.name}</span>
                        <input type="checkbox" checked={tagDropdownSelection.map(String).includes(String(tag.id))} readOnly />
                      </div>
                    ))
                  )}
                </div>
                <div style={{ padding: '8px 16px', borderTop: '1px solid #eee', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <button className="header-btn" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => { onOpenTagSelector({ mode: 'manage' }); setShowTagDropdown(false); }}>Gestionar</button>
                  <button className="header-btn active" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={handleSaveTagDropdown}>Guardar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>


      <div className="messages-container" ref={messagesContainerRef}>
        {/* Indicador de carga de mensajes más viejos */}
        {hasMoreMessages && (
          <div style={{
            textAlign: 'center',
            padding: '8px',
            fontSize: '12px',
            color: '#8696a0'
          }}>
            {loadingMoreMessages
              ? '⏳ Cargando mensajes anteriores...'
              : '⬆ Sube para ver mensajes anteriores'
            }
          </div>
        )}
        {messages.length === 0 ? (
          <div className="no-chat-selected">
            <p>No hay mensajes aún. ¡Envía el primero!</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const showDateDivider = (() => {
              if (index === 0) return true;
              const current = new Date(msg.created_at);
              const prev = new Date(messages[index - 1].created_at);
              if (isNaN(current.getTime()) || isNaN(prev.getTime())) return false;

              return current.toDateString() !== prev.toDateString();
            })();

            return (
              <React.Fragment key={msg.id}>
                {showDateDivider && (
                  <div style={{
                    textAlign: 'center',
                    margin: '10px 0',
                    position: 'sticky',
                    top: '10px',
                    zIndex: 5,
                    pointerEvents: 'none'
                  }}>
                    <span style={{
                      backgroundColor: '#fff',
                      color: '#54656f',
                      padding: '5px 12px',
                      borderRadius: '8px',
                      fontSize: '12.5px',
                      fontWeight: '500',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                      display: 'inline-block',
                      border: '1px solid #e9edef'
                    }}>
                      {(() => {
                        const date = new Date(msg.created_at);
                        if (isNaN(date.getTime())) return '';

                        const today = new Date();
                        const yesterday = new Date(today);
                        yesterday.setDate(yesterday.getDate() - 1);

                        if (date.toDateString() === today.toDateString()) return 'HOY';
                        if (date.toDateString() === yesterday.toDateString()) return 'AYER';

                        // Capitalize logic
                        const dateStr = date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
                        return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
                      })()}
                    </span>
                  </div>
                )}
                <div
                  id={`msg-${msg.id}`}
                  className={`message-row ${msg.direction}`}
                  style={{
                    display: 'flex',
                    justifyContent: msg.direction === 'incoming' ? 'flex-start' : 'flex-end',
                    alignItems: 'center',
                    marginBottom: '4px',
                    padding: '0 10px',
                    position: 'relative',
                    borderRadius: '8px',
                    transition: 'background-color 0.6s ease-out',
                    backgroundColor: highlightedMessageId === msg.id ? 'rgba(0,168,132,0.22)' : 'transparent'
                  }}
                  onMouseEnter={() => setHoveredMessage(msg.id)}
                  onMouseLeave={() => setHoveredMessage(null)}
                >



                  <div
                    className={`message-bubble ${msg.direction === 'incoming' ? 'incoming' : 'outgoing'}`}
                    style={{ position: 'relative' }}
                  >
                    {renderContent(msg)}
                    <div className="message-time">
                      {formatTime(msg.created_at || msg.createdAt)}
                      {msg.direction === 'outgoing' && (
                        <span style={{ marginLeft: '4px' }}>
                          {msg.status === 'sent' ? '✓' : msg.status === 'read' ? '✓✓' : '○'}
                        </span>
                      )}
                    </div>
                    {msg.reaction && (
                      <div style={{
                        position: 'absolute',
                        bottom: '-10px',
                        right: msg.direction === 'incoming' ? 'auto' : '-5px',
                        left: msg.direction === 'incoming' ? '-5px' : 'auto',
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        padding: '2px 5px',
                        fontSize: '14px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        zIndex: 2,
                        border: '1px solid #eee'
                      }}>
                        {msg.reaction}
                      </div>
                    )}
                  </div>



                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Reply Banner */}
      {
        replyingTo && (
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#f0f2f5',
            borderLeft: '4px solid #00a884',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '5px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 'bold', color: '#00a884', fontSize: '13px' }}>
                Respondiendo a {replyingTo.direction === 'outgoing' ? 'ti' : conversation.name}
              </span>
              <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
                {replyingTo.type === 'text' ? replyingTo.body : (
                  replyingTo.type === 'image' ? '📷 Foto' :
                    replyingTo.type === 'video' ? '🎥 Video' :
                      replyingTo.type === 'document' ? '📄 Documento' :
                        replyingTo.type === 'audio' ? '🎵 Audio' : 'Mensaje'
                )}
              </span>
            </div>
            <button onClick={() => setReplyingTo(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#666' }}>
              ✕
            </button>
          </div>
        )
      }

      <div className="input-container" style={{ position: 'relative' }}>
        {/* Banner de Edición */}
        {editingMessage && (
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#f0f2f5',
            borderLeft: '4px solid #00a884',
            padding: '8px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 5,
            borderTop: '1px solid #e9edef'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontWeight: 'bold', color: '#00a884', fontSize: '13px' }}>Editando mensaje</span>
              <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>
                {editingMessage.type === 'text' ? editingMessage.body : 'Archivo multimedia'}
              </span>
            </div>
            <button
              onClick={() => { setEditingMessage(null); setMessageText(''); }}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', color: '#666' }}
            >
              ✕
            </button>
          </div>
        )}

        {showEmojiPicker && (
          <div style={{ position: 'absolute', bottom: '80px', left: '20px', zIndex: 10 }}>
            <EmojiPicker
              onEmojiClick={(emojiData) => {
                setMessageText(prev => prev + emojiData.emoji);
              }}
              width={300}
              height={400}
              previewConfig={{ showPreview: false }}
              searchDisabled={false}
              skinTonesDisabled={true}
            />
          </div>
        )}

        {/* Media Preview Modal */}
        {previewFile && (
          <MediaPreview
            file={previewFile}
            onClose={() => { setPreviewFile(null); if (document.getElementById('file-upload')) document.getElementById('file-upload').value = ''; }}
            onSend={handleConfirmSendMedia}
          />
        )}

        {/* ... existing input container ... */}
        <input
          type="file"
          id="file-upload"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.webp"
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              setPreviewFile(e.target.files[0]);
            }
          }}
        />

        <div className="input-action-buttons">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className={`input-action-btn ${showEmojiPicker ? 'active' : ''}`}
            title="Emojis"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M12,2C6.48,2,2,6.48,2,12s4.48,10,10,10s10-4.48,10-10S17.52,2,12,2z M12,20c-4.41,0-8-3.59-8-8s3.59-8,8-8s8,3.59,8,8 S16.41,20,12,20z M15.5,11c0.83,0,1.5-0.67,1.5-1.5S16.33,8,15.5,8S14,8.67,14,9.5S14.67,11,15.5,11z M8.5,11 c0.83,0,1.5-0.67,1.5-1.5S9.33,8,8.5,8S7,8.67,7,9.5S7.67,11,8.5,11z M12,17.5c2.33,0,4.31-1.46,5.11-3.5H6.89 C7.69,16.04,9.67,17.5,12,17.5z"></path>
            </svg>
          </button>

          <button
            onClick={() => document.getElementById('file-upload').click()}
            className="input-action-btn"
            title="Adjuntar archivo"
            disabled={sending || editingMessage}
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M16.5,6v11.5c0,2.21-1.79,4-4,4s-4-1.79-4-4V5c0-1.38,1.12-2.5,2.5-2.5s2.5,1.12,2.5,2.5v10.5c0,0.55-0.45,1-1,1 s-1-0.45-1-1V6H10v9.5c0,1.66,1.34,3,3,3s3-1.34,3-3V5c0-2.48-2.02-4.5-4.5-4.5S7,2.52,7,5v12.5c0,3.31,2.69,6,6,6s6-2.69,6-6V6H16.5z"></path>
            </svg>
          </button>
        </div>
        <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {showQuickRepliesDropdown && (
            <div
              ref={quickRepliesListRef}
              style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                marginBottom: '6px',
                maxHeight: '220px',
                overflowY: 'auto',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                border: '1px solid #e9edef',
                zIndex: 20
              }}
            >
              <div style={{ padding: '8px 12px', fontSize: '12px', color: '#667781', borderBottom: '1px solid #f0f2f5' }}>
                Respuestas rápidas — elige una
              </div>
              {quickRepliesList.map((q) => (
                <div
                  key={q.id}
                  onClick={() => selectQuickReply(q)}
                  style={{
                    padding: '10px 14px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    borderBottom: '1px solid #f0f2f5',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f6f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  {q.image_url && (
                    <img src={q.image_url} alt="" style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.target.style.display = 'none'; }} />
                  )}
                  <span>{q.text ? (q.text.length > 80 ? q.text.slice(0, 80) + '…' : q.text) : '(imagen)'}</span>
                </div>
              ))}
            </div>
          )}

          {isRecording ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px',
              backgroundColor: 'rgba(255, 0, 0, 0.05)', borderRadius: '24px', border: '1px solid rgba(255,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d32f2f', fontWeight: 'bold' }}>
                <div style={{
                  width: '10px', height: '10px', backgroundColor: '#d32f2f', borderRadius: '50%',
                  animation: 'blink 1.5s infinite'
                }}></div>
                Grabando... {formatRecordingTime(recordingTime)}
              </div>
              <div style={{ display: 'flex', gap: '15px' }}>
                <button
                  onClick={cancelRecording}
                  className="input-action-btn"
                  style={{ color: '#ea0038' }}
                  title="Cancelar grabación"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={messageText}
              onChange={(e) => {
                setMessageText(e.target.value);
                // Auto-adjust height
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
              }}
              onKeyDown={(e) => {
                if (showQuickRepliesDropdown && e.key === 'Escape') {
                  setShowQuickRepliesDropdown(false);
                  setMessageText(prev => prev.replace(/\/[^/]*$/, ''));
                  return;
                }
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (editingMessage) handleSendEdited();
                  else handleSend();
                }
              }}
              placeholder="Escribe un mensaje... (escribe / para respuestas rápidas)"
              disabled={sending}
              rows={1}
              style={{
                resize: 'none',
                minHeight: '45px',
                maxHeight: '150px',
                padding: '12px 18px',
                borderRadius: '24px',
                border: 'none',
                backgroundColor: '#fff',
                fontFamily: 'inherit',
                fontSize: '15px',
                overflowY: 'auto',
                lineHeight: '1.5',
                boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          )}

        </div>

        {isRecording ? (
          <button
            onClick={() => stopRecording(true)}
            disabled={sending}
            style={{
              background: '#00a884', color: 'white', border: 'none', borderRadius: '50%',
              width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', marginLeft: '10px', fontSize: '1.2rem'
            }}
            title="Enviar grabación"
          >
            ➤
          </button>
        ) : (!messageText.trim() && !editingMessage && !sending ? (
          <button
            onClick={startRecording}
            className="input-action-btn"
            style={{ padding: '0 10px' }}
            title="Grabar nota de voz"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            </svg>
          </button>
        ) : (
          <button 
            onClick={editingMessage ? handleSendEdited : handleSend} 
            disabled={sending || !messageText.trim()}
            className="send-btn"
          >
            {sending ? '...' : (editingMessage ? 'Guardar' : 'Enviar')}
          </button>
        ))}
      </div>

    </div>
  );
}

export default ChatArea;
